import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, MicOff, Trash2, AlertCircle, Shield, Wifi, WifiOff, Loader2 } from 'lucide-react';

/**
 * VoiceInput — Project Sovereign
 * 
 * Smart Voice Input Component with dual-engine speech recognition:
 * 1. PRIMARY: Local Whisper WASM (via @xenova/transformers) — all processing on-device
 *    → No audio data leaves the browser → NIS2 compliant
 * 2. FALLBACK: Web Speech API (browser native) — for browsers without WASM support
 * 
 * Same external API as before: { value, onChange, placeholder, className, minHeight }
 */
const VoiceInput = ({ value, onChange, placeholder, className = '', minHeight = 'h-32' }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [isModelLoading, setIsModelLoading] = useState(false);
    const [modelProgress, setModelProgress] = useState(0);
    const [isModelReady, setIsModelReady] = useState(false);
    const [useWhisper, setUseWhisper] = useState(true); // default: try Whisper
    const [error, setError] = useState(null);
    const [engine, setEngine] = useState('detecting'); // 'whisper' | 'webspeech' | 'detecting'

    const recognitionRef = useRef(null); // Web Speech API
    const workerRef = useRef(null);     // Whisper worker
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioContextRef = useRef(null);

    // ─── Whisper Worker Initialization ───────────────────────────────
    useEffect(() => {
        if (!useWhisper) return;

        try {
            workerRef.current = new Worker(
                new URL('../../workers/whisperWorker.js', import.meta.url),
                { type: 'module' }
            );

            workerRef.current.onmessage = (event) => {
                const { type, status, progress, text, error: workerError } = event.data;

                switch (type) {
                    case 'status':
                        if (status === 'loading' || status === 'downloading') {
                            setIsModelLoading(true);
                            setModelProgress(progress || 0);
                        } else if (status === 'ready') {
                            setIsModelLoading(false);
                            setIsModelReady(true);
                            setModelProgress(100);
                            setEngine('whisper');
                        } else if (status === 'transcribing') {
                            setIsTranscribing(true);
                        }
                        break;

                    case 'result':
                        setIsTranscribing(false);
                        if (text) {
                            const trimmed = text.trim();
                            if (trimmed) {
                                const newValue = value ? value + ' ' + trimmed : trimmed;
                                onChange({ target: { value: newValue } });
                            }
                        }
                        break;

                    case 'error':
                        console.warn('Whisper Worker Error, falling back to Web Speech:', workerError);
                        setIsTranscribing(false);
                        setIsModelLoading(false);
                        // Fall back to Web Speech API
                        setUseWhisper(false);
                        setEngine('webspeech');
                        break;
                }
            };

            workerRef.current.onerror = () => {
                console.warn('Whisper Worker failed to load, falling back to Web Speech API');
                setUseWhisper(false);
                setEngine('webspeech');
            };

            // Pre-initialize model (lazy — will download on first use if not cached)
            workerRef.current.postMessage({ type: 'init' });

        } catch (err) {
            console.warn('Cannot create Whisper Worker, falling back to Web Speech API:', err);
            setUseWhisper(false);
            setEngine('webspeech');
        }

        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, [useWhisper]); // eslint-disable-line react-hooks/exhaustive-deps

    // Detect engine on mount if not using whisper
    useEffect(() => {
        if (!useWhisper) {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            setEngine(SpeechRecognition ? 'webspeech' : 'detecting');
        }
    }, [useWhisper]);

    // ─── Audio Processing (Whisper) ──────────────────────────────────
    const processAudioBlob = useCallback(async (blob) => {
        const arrayBuffer = await blob.arrayBuffer();

        if (!audioContextRef.current) {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 16000
            });
        }

        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
        const channelData = audioBuffer.getChannelData(0);

        // Resample to 16kHz if needed
        if (audioBuffer.sampleRate !== 16000) {
            const ratio = audioBuffer.sampleRate / 16000;
            const newLength = Math.floor(channelData.length / ratio);
            const samples = new Float32Array(newLength);
            for (let i = 0; i < newLength; i++) {
                samples[i] = channelData[Math.floor(i * ratio)];
            }
            return samples;
        }
        return channelData;
    }, []);

    // ─── Whisper Recording ───────────────────────────────────────────
    const startWhisperRecording = useCallback(async () => {
        setError(null);
        audioChunksRef.current = [];

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true }
        });

        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/ogg'
        });

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                audioChunksRef.current.push(event.data);
            }
        };

        mediaRecorder.onstop = async () => {
            stream.getTracks().forEach(track => track.stop());

            if (audioChunksRef.current.length === 0) {
                setError('Geen audio opgenomen');
                return;
            }

            const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

            try {
                const audioData = await processAudioBlob(audioBlob);
                setIsTranscribing(true);
                workerRef.current.postMessage({ type: 'transcribe', audioData });
            } catch (err) {
                setError('Fout bij audio verwerking: ' + err.message);
                setIsTranscribing(false);
            }
        };

        mediaRecorderRef.current = mediaRecorder;
        mediaRecorder.start(1000);
        setIsRecording(true);

        if (navigator.vibrate) navigator.vibrate(50);
    }, [processAudioBlob]);

    // ─── Web Speech API Recording ────────────────────────────────────
    const startWebSpeechRecording = useCallback(() => {
        setError(null);
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            setError('Spraakherkenning niet ondersteund in deze browser');
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = 'nl-NL';
        recognition.continuous = true;
        recognition.interimResults = false;

        recognition.onresult = (event) => {
            let transcript = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                if (event.results[i].isFinal) {
                    transcript += event.results[i][0].transcript;
                }
            }
            if (transcript) {
                const newValue = value ? value + ' ' + transcript.trim() : transcript.trim();
                onChange({ target: { value: newValue } });
            }
        };

        recognition.onerror = (e) => {
            console.error('Speech error:', e.error);
            if (e.error === 'not-allowed') {
                setError('Geen toegang tot microfoon');
            }
            setIsRecording(false);
        };

        recognition.onend = () => setIsRecording(false);

        recognitionRef.current = recognition;
        recognition.start();
        setIsRecording(true);

        if (navigator.vibrate) navigator.vibrate(50);
    }, [value, onChange]);

    // ─── Start / Stop ────────────────────────────────────────────────
    const startRecording = useCallback(async () => {
        try {
            if (useWhisper && isModelReady) {
                await startWhisperRecording();
            } else {
                startWebSpeechRecording();
            }
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                setError('Geen toegang tot microfoon. Controleer browserinstellingen.');
            } else {
                setError('Fout bij opname starten: ' + err.message);
            }
        }
    }, [useWhisper, isModelReady, startWhisperRecording, startWebSpeechRecording]);

    const stopRecording = useCallback(() => {
        if (useWhisper && mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
        } else if (recognitionRef.current) {
            recognitionRef.current.stop();
            setIsRecording(false);
        }
    }, [useWhisper, isRecording]);

    const handleClear = () => {
        onChange({ target: { value: '' } });
    };

    // ─── Engine Badge ────────────────────────────────────────────────
    const EngineBadge = () => {
        if (engine === 'whisper') {
            return (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium" title="Whisper WASM — Alle audio wordt lokaal verwerkt. Geen data verlaat uw browser.">
                    <Shield size={10} />
                    <WifiOff size={10} />
                    Lokaal (NIS2)
                </span>
            );
        }
        if (engine === 'webspeech') {
            return (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium" title="Web Speech API — Audio wordt via de browser verwerkt (Google/Apple servers)">
                    <Wifi size={10} />
                    Browser API
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">
                <Loader2 size={10} className="animate-spin" />
                Laden...
            </span>
        );
    };

    // ─── Model Loading State ─────────────────────────────────────────
    const isLoading = isModelLoading || isTranscribing;
    const buttonDisabled = isLoading && !isRecording;

    return (
        <div className="space-y-2">
            {/* Engine indicator + model loading progress */}
            <div className="flex items-center justify-between">
                <EngineBadge />
                {isModelLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <div className="w-24 bg-slate-200 rounded-full h-1.5">
                            <div
                                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${modelProgress}%` }}
                            />
                        </div>
                        <span>{modelProgress}%</span>
                    </div>
                )}
                {isTranscribing && (
                    <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                        <Loader2 size={12} className="animate-spin" />
                        Transcriberen...
                    </span>
                )}
            </div>

            {/* Textarea */}
            <textarea
                value={value}
                onChange={onChange}
                onKeyDown={(e) => e.stopPropagation()}
                onKeyUp={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                placeholder={placeholder}
                className={`w-full p-4 rounded-lg bg-white text-black border-2 ${isRecording ? 'border-red-500 shadow-red-200 shadow-lg' : isTranscribing ? 'border-blue-400 shadow-blue-200 shadow-lg' : 'border-slate-300'} focus:border-blue-500 focus:outline-none resize-none ${minHeight} ${className}`}
            />

            {/* Buttons */}
            <div className="flex gap-3">
                {/* Recording Button */}
                {!isRecording ? (
                    <button
                        type="button"
                        onClick={startRecording}
                        disabled={buttonDisabled}
                        className={`btn flex-1 py-3 shadow-md gap-2 ${buttonDisabled ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-yellow-400 hover:bg-yellow-300 text-black'}`}
                    >
                        {isTranscribing ? (
                            <>
                                <Loader2 size={20} className="animate-spin" />
                                VERWERKEN...
                            </>
                        ) : (
                            <>
                                <Mic size={20} />
                                {engine === 'whisper' ? 'SPRAAK OPNEMEN (LOKAAL)' : 'START SPRAAKOPNAME'}
                            </>
                        )}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={stopRecording}
                        className="btn flex-1 py-3 bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-500/50 gap-2 animate-pulse"
                    >
                        <MicOff size={20} />
                        STOP OPNAME
                    </button>
                )}

                {/* Clear Button - only show when there's text */}
                {value && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="btn px-6 py-3 bg-white text-red-600 border-none shadow-md hover:bg-red-50 gap-2"
                    >
                        <Trash2 size={20} />
                        WIS TEKST
                    </button>
                )}
            </div>

            {/* Error Message */}
            {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}
        </div>
    );
};

export default VoiceInput;
